/**
 * Every user-visible string on the page.
 *
 * Copy is the source of truth here — components read from this file and hold no
 * prose of their own. Rewrite freely; nothing else needs to change.
 *
 * (Exception: the strings in index.html — <title>, meta description, the
 * noscript block — are static HTML and cannot be read from TS at build time.
 * Keep those in sync by hand.)
 *
 * Factual anchors that are NOT copy and must stay literal:
 *   - the `entities` lists, from parse_entity_type() in
 *     crates/thinai-graph-knuckles/src/graphql/schema.rs
 *   - the DORA elite/high thresholds, from
 *     docs-public/metrics/dora-overview.md
 */

/* ── Destinations ─────────────────────────────────────────────────────────────
   This page is served at https://thinai.dev (see the canonical tag in
   index.html). There is still no dashboard and no docs site behind it, and
   Nuvai/Thinai is a private repository — so every CTA and nav link points at an
   in-page anchor rather than shipping a dead link.

   When those hosts exist, set the two constants and restore the CTAs. */

export const DASHBOARD_URL: string | null = null
export const DOCS_URL: string | null = null

export const brand = {
  name: 'Thinai',
  wordmark: 'thinai',
  tagline: 'Development orchestration for AI teams',
  description:
    'Connects code management, product planning, and delivery through a shared knowledge graph.',
}

/* ── Nav ────────────────────────────────────────────────────────────────────── */

export const nav = {
  links: [
    { label: 'Landscapes', href: '#landscapes' },
    { label: 'Agents', href: '#agents' },
    { label: 'Metrics', href: '#metrics' },
    { label: 'Ecosystem', href: '#ecosystem' },
  ],
  cta: { label: 'Get started', href: '#get-started' },
  logoAria: 'Thinai home',
  menuOpenAria: 'Open menu',
  menuCloseAria: 'Close menu',
}

/* ── Hero ───────────────────────────────────────────────────────────────────── */

export const hero = {
  eyebrow: 'Thinai',
  title: 'Five landscapes. One connected graph.',
  subtitle:
    'Your code is in one place, issues in another, and decisions somewhere else. Thinai connects them, giving AI agents the context they need to work across your development process.',
  sceneTabs: {
    landscapes: 'Landscapes',
    graph: 'Knowledge graph',
  },
  sceneCaptions: {
    landscapes: 'Follow a change from product planning through to production.',
    graph:
      'Repositories, issues, decisions, and everything around them become part of one queryable graph.',
  },
  primaryCta: { label: 'Explore the platform', href: '#landscapes' },
  ghostCta: { label: 'Meet the agents', href: '#agents' },
}

/* ── Section: the five landscapes ─────────────────────────────────────────────
   Not a metaphor laid over the product — the domain taxonomy. parse_entity_type
   maps every entity type in the system to exactly one of these five, and the
   Rust crates are organised the same way (thinai-planning/src/kurinji.rs,
   thinai-api's "(Kurinji domain)" planning routes). */

export const landscapesSection = {
  eyebrow: 'The five landscapes',
  title: 'Five domains. One platform.',
  intro:
    'Thinai takes its name from the classical Tamil idea of five interconnected landscapes. Here, that idea is part of the system itself: every entity belongs to one of the five domains, and the knowledge graph connects them all.',
}

export interface Landscape {
  id: string
  index: string
  name: string
  domain: string
  tagline: string
  /**
   * NOT CURRENTLY RENDERED. Verbatim entity names from the GraphQL schema
   * (parse_entity_type). The cards are summary-only; this list was cut along
   * with the paragraph because five columns of detail read as a wall of text.
   * Still the accurate statement of what each domain owns — wire it back into
   * StackSection.tsx, or use it elsewhere, if it is wanted.
   */
  entities: string
  /** NOT CURRENTLY RENDERED. See `entities` above. */
  body: string
  glyph: string
  role: 'source' | 'agent' | 'engine' | 'graph' | 'output'
}

export const landscapes: Landscape[] = [
  {
    id: 'kurinji',
    index: '01',
    name: 'Kurinji',
    domain: 'Product',
    tagline: 'From idea to something the team can build',
    entities: 'Epic · Feature · Story · Task · Sprint · Roadmap · OKR',
    body: 'The planning hierarchy, connected all the way to the commits that implement it. Agents can spot gaps in requirements and help draft release notes.',
    glyph: 'peak',
    role: 'source',
  },
  {
    id: 'mullai',
    index: '02',
    name: 'Mullai',
    domain: 'Knowledge',
    tagline: 'Decisions stay connected to the work',
    entities: 'ADR · Documentation · API · Standard · Runbook · TechDebt',
    body: 'Institutional knowledge linked directly to the code it describes, making outdated documentation easier to see before it becomes a problem.',
    glyph: 'book',
    role: 'agent',
  },
  {
    id: 'marutam',
    index: '03',
    name: 'Marutam',
    domain: 'Code',
    tagline: 'Where the work gets built',
    entities: 'Repository · Commit · PullRequest · Branch',
    body: 'Git hosting connected to the graph. Reviews can take the entire repository into account instead of looking at the diff in isolation.',
    glyph: 'branch',
    role: 'engine',
  },
  {
    id: 'neytal',
    index: '04',
    name: 'Neytal',
    domain: 'Quality',
    tagline: 'Catch problems before they ship',
    entities: 'SecurityPolicy · ComplianceRule · Benchmark',
    body: 'Security and compliance are part of the development model, with rules enforced as gates before a human review.',
    glyph: 'shield',
    role: 'graph',
  },
  {
    id: 'palai',
    index: '05',
    name: 'Pālai',
    domain: 'Delivery',
    tagline: 'From a release to what happens after it',
    entities: 'Pipeline · Deployment · Environment · Incident',
    body: 'Release orchestration with incidents tied back to the deployments that caused them.',
    glyph: 'rocket',
    role: 'output',
  },
]

/* ── Section: AI agents ───────────────────────────────────────────────────────
   The roster named in docs-public/features/ai-agents.md. */

export const agentsSection = {
  eyebrow: 'AI agents',
  title: 'Agents with the context to do the job.',
  intro:
    'An agent can only work with what it can see. Thinai connects repositories, decisions, ADRs, and the rest of your development context in one graph, so agents can work from the codebase rather than a single file or diff.',
}

export interface Agent {
  name: string
  verb: string
  body: string
}

export const agents: Agent[] = [
  {
    name: 'Code Review',
    verb: 'review',
    body: 'Reviews every pull request for quality, security, and established patterns, with the repository as context rather than just the diff.',
  },
  {
    name: 'Test Generation',
    verb: 'cover',
    body: 'Generates unit tests for new code, including edge cases and security scenarios that are easy to miss when deadlines get tight.',
  },
  {
    name: 'Security Scan',
    verb: 'scan',
    body: 'Scans continuously for vulnerabilities, follows advisories in third-party dependencies, and provides concrete remediation steps.',
  },
  {
    name: 'Documentation',
    verb: 'record',
    body: 'Keeps API references, guides, and runbooks aligned with the code as it changes.',
  },
  {
    name: 'Deployment',
    verb: 'ship',
    body: 'Runs releases against established patterns, watches system health, and rolls back when error rates start climbing.',
  },
  {
    name: 'Planning',
    verb: 'scope',
    body: 'Estimates work using comparable past projects, surfaces blockers and dependencies, and helps balance work across the team.',
  },
]

/* ── Section: delivery metrics ────────────────────────────────────────────────
   Thresholds quoted from docs-public/metrics/dora-overview.md. Relabel the rows
   freely; changing the values makes them wrong. */

export const metricsSection = {
  eyebrow: 'Delivery metrics',
  title: 'See how your delivery is actually performing.',
  intro:
    'Four DORA metrics, calculated from the same graph used by the agents. Elite and High thresholds use the published reference bands, giving you a benchmark alongside the trend.',
  footnote: 'Thresholds from the DORA reference bands',
  eliteLabel: 'Elite',
  highLabel: 'High',
}

export interface DoraMetric {
  name: string
  question: string
  elite: string
  high: string
}

export const dora: DoraMetric[] = [
  {
    name: 'Deployment Frequency',
    question: 'How often do you deploy to production?',
    elite: 'Multiple times per day',
    high: 'Daily to weekly',
  },
  {
    name: 'Lead Time for Changes',
    question: 'How long from commit to production?',
    elite: 'Under 1 hour',
    high: 'Under 1 week',
  },
  {
    name: 'Change Failure Rate',
    question: 'What share of deployments cause failures?',
    elite: '0–5%',
    high: '5–15%',
  },
  {
    name: 'Time to Recovery',
    question: 'How fast do you restore service?',
    elite: 'Under 1 hour',
    high: 'Under 1 day',
  },
]

/* ── Section: Nuvai ecosystem ─────────────────────────────────────────────────
   From CLAUDE.md's ecosystemIntegration.nuvaiServices. */

export const ecosystemSection = {
  eyebrow: 'Nuvai ecosystem',
  title: 'Shared foundations, connected products.',
  intro:
    'Thinai is part of a larger platform. Storage, code intelligence, artifacts, model serving, and identity are shared services, so each product can build on the same foundations instead of rebuilding them.',
  filler: {
    label: '+ more',
    body: 'Every service uses the same underlying types, so adding another service means connecting it rather than rebuilding the pieces around it.',
  },
}

export interface EcosystemItem {
  input: string
  name: string
  output: string
  body: string
  role: 'source' | 'agent' | 'engine' | 'graph' | 'output'
}

export const ecosystem: EcosystemItem[] = [
  {
    input: 'types',
    name: 'nuvai-core',
    output: 'shared',
    body: 'Universal types and infrastructure used across Nuvai services.',
    role: 'source',
  },
  {
    input: 'storage',
    name: 'Knuckles',
    output: 'query',
    body: 'Hybrid storage for relational, graph, vector, and temporal data through one query language.',
    role: 'graph',
  },
  {
    input: 'code',
    name: 'NuCodeSense',
    output: 'insight',
    body: 'Code intelligence and static analysis streamed into the graph as the codebase changes.',
    role: 'agent',
  },
  {
    input: 'artifacts',
    name: 'Registry',
    output: 'distribute',
    body: 'Stores and distributes artifacts built across the ecosystem.',
    role: 'engine',
  },
  {
    input: 'graphs',
    name: 'Knyra',
    output: 'analytics',
    body: 'Graph integration and analytics for the connected development model.',
    role: 'graph',
  },
  {
    input: 'models',
    name: 'NuCraft',
    output: 'inference',
    body: 'Model deployment and serving for agents working against the graph.',
    role: 'engine',
  },
  {
    input: 'identity',
    name: 'NuvaiAuth',
    output: 'access',
    body: 'Centralised authentication, RBAC, OAuth, and SSO across the product suite.',
    role: 'output',
  },
]

/* ── Section: get started ─────────────────────────────────────────────────────
   The onboarding path from docs-public/getting-started/quickstart.md. There is
   no public dashboard to send anyone to yet, so this describes the flow rather
   than driving a signup. */

export const getStartedSection = {
  eyebrow: 'Get started',
  title: 'Connect a repo. Put the agents to work.',
  beforeLabel: 'Before you start',
  importLabel: 'What the import does',
  importSteps: [
    'Analyses your codebase structure',
    'Imports git history, issues, and pull requests',
    'Builds the knowledge graph across all five domains',
    "Detects patterns in your team's work for the agents",
  ],
  rolloutNote:
    'Docs and dashboard access are still being prepared. Everything above describes the platform as built.',
}

export interface Step {
  n: string
  title: string
  body: string
  detail: string
}

export const steps: Step[] = [
  {
    n: '01',
    title: 'Create your account',
    body: 'Sign in with GitHub OAuth, email and password, or SSO on an enterprise plan.',
    detail: 'GitHub OAuth · Email · SSO',
  },
  {
    n: '02',
    title: 'Connect your repositories',
    body: 'Authorise the GitHub integration and choose the repositories you want to import. Start with one or two.',
    detail: 'Import typically completes in 5–15 minutes',
  },
  {
    n: '03',
    title: 'Enable your first agents',
    body: 'Enable Code Review or Test Generation from the agent marketplace, then choose when they should run.',
    detail: 'Per-PR triggers · Configurable severity',
  },
]

export const prerequisites = [
  'A GitHub account with repositories to import',
  'Admin access to your team’s development tools',
  'A modern browser — Chrome, Firefox, Safari or Edge',
]

/* ── Footer ───────────────────────────────────────────────────────────────────
   Columns are in-page anchors for now; a Source column returns when the
   repository is public. */

export const footer = {
  headline: 'Ready to see the whole picture?',
  blurb:
    'Bring your repositories into one graph and give your agents the context they need to work across them.',
  ctaPrimary: { label: 'See how to start', href: '#get-started' },
  ctaSecondary: { label: 'Back to the domains', href: '#landscapes' },
  columns: [
    {
      title: 'Platform',
      links: [
        { label: 'Landscapes', href: '#landscapes' },
        { label: 'AI Agents', href: '#agents' },
        { label: 'Metrics', href: '#metrics' },
        { label: 'Ecosystem', href: '#ecosystem' },
      ],
    },
    {
      title: 'The five domains',
      links: [
        { label: 'Kurinji — Product', href: '#landscapes' },
        { label: 'Mullai — Knowledge', href: '#landscapes' },
        { label: 'Marutam — Code', href: '#landscapes' },
        { label: 'Neytal — Quality', href: '#landscapes' },
        { label: 'Pālai — Delivery', href: '#landscapes' },
      ],
    },
    {
      title: 'Getting started',
      links: [
        { label: 'How to start', href: '#get-started' },
        { label: 'Prerequisites', href: '#get-started' },
        { label: 'Back to top', href: '#top' },
      ],
    },
  ],
  // Rendered as text, not links — there are no policy pages yet, and a dead
  // link is worse than a quiet label.
  legal: ['Privacy', 'Terms', 'Security'],
  copyright: '© 2026 Thinai. Development orchestration for AI teams.',
}
